import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { format, isValid, parseISO } from "date-fns"
import { DATE_DISPLAY_FORMAT, DATETIME_DISPLAY_FORMAT, DATETIME_DISPLAY_FORMAT_WITH_SECONDS } from "@/shared/constants/form-messages"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function toUpperSafe(value: string | null | undefined): string {
  if (value == null) return ""
  return value.toUpperCase()
}

export function formatDate(date: Date): string {
  return format(date, DATE_DISPLAY_FORMAT)
}

export function formatDateString(dateString: string | null, formatPattern: string = DATE_DISPLAY_FORMAT): string {
  if (!dateString) return 'N/A'
  try {
    return format(new Date(dateString), formatPattern)
  } catch (error) {
    return dateString
  }
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount)
}

export function formatCurrencyDecimal(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

export function formatDateGB(dateString?: string): string {
  if (!dateString) return 'YYYY/MM/DD'
  try {
    const date = parseISO(dateString)
    if (!isValid(date)) return 'YYYY/MM/DD'
    return format(date, DATE_DISPLAY_FORMAT)
  } catch {
    return 'YYYY/MM/DD'
  }
}

export function formatDateTimeGB(dateString?: string | null, fallback: string = 'YYYY/MM/DD HH:mm', options?: { withSeconds?: boolean }): string {
  if (!dateString) return fallback
  try {
    const date = parseISO(dateString)
    if (!isValid(date)) return fallback
    const pattern = options?.withSeconds ? DATETIME_DISPLAY_FORMAT_WITH_SECONDS : DATETIME_DISPLAY_FORMAT
    return format(date, pattern)
  } catch {
    return fallback
  }
}

export function getDisplayPhoneNumber(phoneNumber: string): string {
  if (!phoneNumber) return ''
  // Add spacing every 3 digits
  return phoneNumber.replace(/(\d{3})(?=\d)/g, '$1 ')
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + "..."
}

export function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

// Auth/permission helpers
export function isAuthError(err: unknown): boolean {
  const e = err as { response?: { errorCode?: string; statusCode?: number }; message?: string } | undefined
  return (
    e?.response?.errorCode === 'UNAUTHORIZED' ||
    e?.response?.statusCode === 401 ||
    e?.message === 'Token expired'
  )
}

// Chart/date helpers – YYYY/MM/DD for consistency
export function formatDateRangeLabel(startIso: string, endIso: string, timeframe: string): string {
  const start = new Date(startIso)
  const end = new Date(endIso)
  const fmt = (d: Date) => format(d, DATE_DISPLAY_FORMAT)

  if (timeframe === 'daily') {
    return fmt(start)
  }
  const sameMonth = start.getUTCFullYear() === end.getUTCFullYear() && start.getUTCMonth() === end.getUTCMonth()
  if (timeframe === 'monthly' || sameMonth) {
    return `${fmt(start)} – ${fmt(end)}`
  }
  return `${fmt(start)} – ${fmt(end)}`
}

// Deterministic color from text (e.g., ORI or workstation name)
export function getDeterministicColorFromText(text: string): string {
  const colorPalette = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1', '#14B8A6', '#F43F5E',
  ]
  const hash = text.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc)
  }, 0)
  const colorIndex = Math.abs(hash) % colorPalette.length
  return colorPalette[colorIndex]
}

export function isBlobUrl(url: string | undefined): url is TBlobUrl {
  if (!url) { 
    return false;
  }

  return url.startsWith('blob:') 
}

export function revokeBlobURL(url: TBlobUrl) {
  return URL.revokeObjectURL(url)
}

export function checkIsNotEmptyArray<T>(data: T[]): data is [T, ...T[]] {
  const { length } = data
  return Boolean(length)
}
